import { type ReactNode, forwardRef } from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { zDropdown } from "@/lib/z";
import { cn } from "@/lib/cn";

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuGroup = DropdownMenuPrimitive.Group;
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

export const DropdownMenuContent = forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      style={{ zIndex: zDropdown }}
      className={cn(
        "min-w-[10rem] overflow-hidden rounded-lg border border-border bg-bg-elevated p-1 text-fg-primary shadow-md",
        "origin-[var(--radix-dropdown-menu-content-transform-origin)] transition-[opacity,transform] duration-150",
        "data-[state=open]:opacity-100 data-[state=closed]:opacity-0",
        "data-[state=open]:scale-100 data-[state=closed]:scale-95",
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = "DropdownMenuContent";

export interface DropdownMenuItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> {
  /** Style as a destructive action (danger color). */
  destructive?: boolean;
  /** Optional leading icon. */
  icon?: ReactNode;
  /** Optional trailing shortcut hint. */
  shortcut?: ReactNode;
}

export const DropdownMenuItem = forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(({ className, destructive = false, icon, shortcut, children, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none",
      "transition-colors focus:bg-bg-secondary data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      destructive
        ? "text-danger focus:bg-danger/10 focus:text-danger"
        : "text-fg-primary",
      className,
    )}
    {...props}
  >
    {icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center">{icon}</span>}
    <span className="flex-1 truncate">{children}</span>
    {shortcut && (
      <span className="ml-auto pl-3 text-xs text-fg-muted">{shortcut}</span>
    )}
  </DropdownMenuPrimitive.Item>
));
DropdownMenuItem.displayName = "DropdownMenuItem";

export const DropdownMenuSeparator = forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-border", className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

export const DropdownMenuLabel = forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-xs font-medium text-fg-muted", className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";

/* ---------------------------------------------------------------- ActionMenu */

export interface ActionMenuItem {
  /** Visible item text. */
  label: ReactNode;
  onSelect: () => void;
  icon?: ReactNode;
  shortcut?: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
  /** Insert a separator before this item. */
  separatorBefore?: boolean;
}

export interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Override the default MoreHorizontal trigger. */
  trigger?: ReactNode;
  /** Accessible label for the default icon trigger. */
  label?: string;
  align?: DropdownMenuPrimitive.DropdownMenuContentProps["align"];
}

/**
 * ActionMenu — row overflow menu (DESIGN-SYSTEM §5, §6.1).
 * The main de-clutter tool for table rows: a `MoreHorizontal` icon button that
 * opens an item list (with optional destructive section + separators).
 */
export function ActionMenu({
  items,
  trigger,
  label = "Действия",
  align = "end",
}: ActionMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="icon" aria-label={label}>
            <MoreHorizontal size={18} strokeWidth={2} aria-hidden="true" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        {items.map((item, i) => (
          <div key={i}>
            {item.separatorBefore && i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              destructive={item.destructive}
              icon={item.icon}
              shortcut={item.shortcut}
              disabled={item.disabled}
              onSelect={item.onSelect}
            >
              {item.label}
            </DropdownMenuItem>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
ActionMenu.displayName = "ActionMenu";
